import { KeyCodes } from '@fluentui/react/lib/Utilities';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import * as React from 'react';
import {
    createSurfaceItemFromModel,
    getDateRange,
    CalendarSurfaceStore,
    toPercentString,
    CalendarSurfaceTransformProps,
} from 'owa-calendar-surface-dategrid';
import {
    addDays,
    addHours,
    addMinutes,
    differenceInMinutes,
    getHours,
    getMinutes,
    isAfter,
    isBefore,
    isEqual,
    isSameDay,
    OwaDate,
    startOfDay,
    subMinutes,
} from 'owa-datetime';
import {
    getDefaultShouldShortenEventDurationStartLate,
    getDefaultShouldShortenEventDurationEndEarly,
} from 'owa-calendar-cloud-settings';
import { getConfiguredDefaultEventDuration } from 'owa-calendar-cloud-settings-utils';
import getDefaultEventDuration from 'owa-session-store/lib/selectors/getDefaultEventDuration';

let styles = require('owa-calendar-surface-dategrid/lib/logic/SelectionStyles.scss');
let classNames = require('classnames/bind').bind(styles);

type ResizeType = 'Top' | 'None' | 'Bottom';

export interface GridSelectionProps {
    surfaceStore: CalendarSurfaceStore;
    config: CalendarSurfaceTransformProps;
    displayDate: OwaDate;
    shouldAdjustStartTimeEndTime?: boolean;
    getSelection: () => CalendarEvent;
    setTimeSelection: (start: OwaDate, end: OwaDate) => void;
    setDateSelection: (date: OwaDate) => void;
    isTouchOnlyDevice: () => boolean;
}

export default function useGridSelectionHooks(props: GridSelectionProps) {
    const [dragClass, setDragClass] = React.useState<string>('');
    const [offset, setOffset] = React.useState<number>(null);
    const resizeTypeRef = React.useRef<ResizeType>();
    let parentElementRef = null as React.MutableRefObject<HTMLElement>;

    const moveSelection = React.useCallback(
        (increment: number, evt: React.KeyboardEvent<unknown>) => {
            let { getSelection, setTimeSelection } = props;

            let selectionItem = getSelection();

            let duration = differenceInMinutes(selectionItem.End, selectionItem.Start);
            let dayMin = startOfDay(selectionItem.Start);
            let dayMax = addMinutes(addDays(dayMin, 1), -duration);

            let start = addMinutes(selectionItem.Start, increment);
            differenceInMinutes(dayMin, start) > 0 && (start = dayMin);
            differenceInMinutes(dayMax, start) < 0 && (start = dayMax);
            let end = addMinutes(start, duration);

            evt.stopPropagation();
            evt.preventDefault();

            setTimeSelection(start, end);
        },
        [props.getSelection, props.setTimeSelection]
    );

    const moveDays = React.useCallback(
        (increment: number, evt: React.KeyboardEvent<unknown>) => {
            let { setTimeSelection, setDateSelection, getSelection, displayDate } = props;
            let event = getSelection();
            let start = addDays(event.Start, increment);
            let end = addDays(event.End, increment);
            setDateSelection(addDays(displayDate, increment));
            setTimeSelection(start, end);
            evt.stopPropagation();
            evt.preventDefault();
        },
        [props.setTimeSelection, props.setDateSelection, props.getSelection, props.displayDate]
    );

    const onKeyDown = React.useCallback(
        (evt: React.KeyboardEvent) => {
            let { surfaceStore } = props;
            evt.keyCode == KeyCodes.up &&
                moveSelection(-surfaceStore.userOptions.hourIncrement, evt);
            evt.keyCode == KeyCodes.down &&
                moveSelection(surfaceStore.userOptions.hourIncrement, evt);
            evt.keyCode == KeyCodes.left && moveDays(-1, evt);
            evt.keyCode == KeyCodes.right && moveDays(1, evt);
        },
        [props.surfaceStore.userOptions.hourIncrement, moveSelection, moveDays]
    );

    const onFocus = React.useCallback(
        (evt: React.FocusEvent<HTMLElement>) => {
            let { config, getSelection, setTimeSelection } = props;
            let selectionItem = getSelection();
            if (selectionItem.IsAllDayEvent) {
                return;
            }

            let columnDate = config.columnProvider.getColumnDate(0);
            if (isSameDay(selectionItem.Start, columnDate)) {
                return;
            }

            let start = addHours(columnDate, getHours(selectionItem.Start));
            start = addMinutes(start, getMinutes(selectionItem.Start));

            let duration = differenceInMinutes(selectionItem.End, selectionItem.Start);
            let end = addMinutes(start, duration);

            setTimeSelection(start, end);
        },
        [props.config, props.getSelection, props.setTimeSelection]
    );

    const makeResizeEdge = React.useCallback(
        (className: string, handler: (ev: React.MouseEvent<HTMLElement>) => void) => {
            let itemClass = classNames(styles.resizableEdge, className, dragClass);
            return <span className={itemClass} onMouseDown={handler} />;
        },
        [dragClass]
    );

    const onMouseUp = React.useCallback((ev: MouseEvent) => {
        document.onmouseup = null;
        document.onmousemove = null;
        document.body.style.cursor = 'default';
        setDragClass('');
    }, []);

    const getCurrentCellRange = React.useCallback(
        (ev: MouseEvent) => {
            const itemsLayout = props.config.itemsLayout;
            let interval = itemsLayout.pointToInterval(
                0,
                ev.pageY,
                parentElementRef?.current?.getBoundingClientRect()
            );

            return getDateRange(interval, props.surfaceStore, props.config.columnProvider);
        },
        [
            parentElementRef,
            props.config.itemsLayout,
            props.surfaceStore,
            props.config.columnProvider,
        ]
    );

    const onResizeMouseMove = React.useCallback(
        (ev: MouseEvent) => {
            let cell = getCurrentCellRange(ev);
            let {
                getSelection,
                setTimeSelection,
                isTouchOnlyDevice,
                shouldAdjustStartTimeEndTime,
            } = props;
            const { Start, End } = getSelection();
            const eventDuration = shouldAdjustStartTimeEndTime
                ? getConfiguredDefaultEventDuration()
                : getDefaultEventDuration();

            if (!isTouchOnlyDevice()) {
                if (isAfter(cell.end, End)) {
                    if (resizeTypeRef.current === 'Top') {
                        // Current cell would shift both start and end, so update to minimal length
                        const newStartTime =
                            shouldAdjustStartTimeEndTime &&
                            getDefaultShouldShortenEventDurationEndEarly()
                                ? isBefore(cell.start, End)
                                    ? cell.start
                                    : subMinutes(End, eventDuration)
                                : subMinutes(End, props.surfaceStore.userOptions.hourIncrement);
                        setTimeSelection(newStartTime, End);
                    } else {
                        // current cell is below event. Update end time and remember resizeBottom as a last operation
                        setTimeSelection(Start, cell.end);
                    }
                } else if (isBefore(cell.start, Start)) {
                    if (resizeTypeRef.current === 'Bottom') {
                        // Current cell would shift both start and end, so update to minimal length
                        const newEndTime =
                            shouldAdjustStartTimeEndTime &&
                            getDefaultShouldShortenEventDurationStartLate()
                                ? isAfter(cell.end, Start)
                                    ? cell.end
                                    : addMinutes(Start, eventDuration)
                                : addMinutes(Start, props.surfaceStore.userOptions.hourIncrement);
                        setTimeSelection(Start, newEndTime);
                    } else {
                        // current cell is above event. Update start time and remember resizeTop as a last operation
                        setTimeSelection(cell.start, End);
                    }
                } else if (resizeTypeRef.current === 'Top') {
                    // current cell is within event and last op was on top edge. Update start time
                    !isEqual(cell.start, Start) && setTimeSelection(cell.start, End);
                } else if (resizeTypeRef.current === 'Bottom') {
                    // current cell is within event and last op was on bottom edge. Update end time
                    !isEqual(cell.end, End) && setTimeSelection(Start, cell.end);
                }
            }
        },
        [
            getCurrentCellRange,
            props.getSelection,
            props.setTimeSelection,
            props.surfaceStore.userOptions.hourIncrement,
            props.isTouchOnlyDevice,
        ]
    );

    const onMouseMove = React.useCallback(
        (ev: MouseEvent) => {
            let {
                getSelection,
                setTimeSelection,
                isTouchOnlyDevice,
                shouldAdjustStartTimeEndTime,
            } = props;

            let { start } = getCurrentCellRange(ev);
            let selectionItem = getSelection();
            let newStart =
                shouldAdjustStartTimeEndTime && getDefaultShouldShortenEventDurationStartLate()
                    ? start
                    : addMinutes(start, offset);

            if (!isEqual(selectionItem.Start, newStart) && !isTouchOnlyDevice()) {
                let duration = differenceInMinutes(selectionItem.End, selectionItem.Start);
                setTimeSelection(newStart, addMinutes(newStart, duration));
            }
        },
        [props.getSelection, props.setTimeSelection, getCurrentCellRange, offset]
    );

    const beginDrag = React.useCallback(
        (ev: React.MouseEvent<HTMLElement>, resizeType: ResizeType) => {
            if (ev.button == 0) {
                ev.stopPropagation();
                document.onmouseup = onMouseUp;
                resizeTypeRef.current = resizeType;
                document.onmousemove = resizeType === 'None' ? onMouseMove : onResizeMouseMove;
            }
        },
        [onMouseUp, onResizeMouseMove, onMouseMove]
    );

    const onMouseDown = React.useCallback(
        (ev: React.MouseEvent<HTMLElement>) => {
            if (ev.button == 0) {
                document.body.style.cursor = 'pointer';
                let { getSelection } = props;
                let offset = differenceInMinutes(
                    getSelection().Start,
                    getCurrentCellRange(ev.nativeEvent).start
                );
                setDragClass(styles.move);
                setOffset(offset);
            }
            beginDrag(ev, 'None');
        },
        [beginDrag, getCurrentCellRange, props.getSelection]
    );

    const onBottomResizeStart = React.useCallback(
        (ev: React.MouseEvent<HTMLElement>) => {
            if (ev.button == 0) {
                document.body.style.cursor = 's-resize';
                setDragClass(styles.bottomResize);
            }
            beginDrag(ev, 'Bottom');
        },
        [beginDrag]
    );

    const onTopResizeStart = React.useCallback(
        (ev: React.MouseEvent<HTMLElement>) => {
            if (ev.button == 0) {
                document.body.style.cursor = 'n-resize';
                setDragClass(styles.topResize);
            }
            beginDrag(ev, 'Top');
        },
        [beginDrag]
    );

    const createSurfaceItemByKey = React.useCallback(
        (helperModel: CalendarEvent) => {
            const { config } = props;
            return createSurfaceItemFromModel(
                helperModel.ItemId.Id,
                helperModel,
                config.columnProvider
            );
        },
        [props.config.columnProvider]
    );

    const createChildren = React.useCallback((): JSX.Element & { key: React.Key } => {
        let { config, getSelection, surfaceStore } = props;

        let selectionItem = getSelection();
        if (selectionItem.IsAllDayEvent) {
            return null;
        }

        let intervals = config.columnProvider.getColumnIntervals(selectionItem);
        if (!intervals || intervals.length == 0) {
            return null;
        }

        let item = createSurfaceItemByKey(selectionItem);
        let itemLayout = config.itemsLayout.getVerticalLayout(intervals[0]);

        let itemConfig = config.getItemConfig(selectionItem);
        let itemClass = classNames(styles.dragBlock, dragClass);
        let itemStyle = {
            top: toPercentString(itemLayout.top),
            bottom: toPercentString(1 - (itemLayout.top + itemLayout.height)),
            left: 0,
            right: 0,
        };

        return (
            <div
                key={'useGridSelectionHook'}
                data-tabid={item.key}
                className={itemClass}
                style={itemStyle}
                onMouseDown={onMouseDown}>
                {
                    // top resize edge
                    makeResizeEdge(styles.topResizable, onTopResizeStart)
                }
                {itemConfig.template(surfaceStore, item.model)}
                {
                    // bottom resize edge
                    makeResizeEdge(styles.bottomResizable, onBottomResizeStart)
                }
            </div>
        );
    }, [
        dragClass,
        makeResizeEdge,
        onMouseDown,
        onBottomResizeStart,
        onTopResizeStart,
        createSurfaceItemByKey,
        props.config.columnProvider,
        props.config.itemsLayout,
        props.config.getItemConfig,
        props.getSelection,
        props.surfaceStore,
    ]);

    return React.useCallback(
        (ref: React.MutableRefObject<HTMLElement>) => {
            parentElementRef = ref;

            return {
                children: createChildren(),
                handlers: {
                    onKeyDown: onKeyDown,
                    onFocus: onFocus,
                },
            };
        },
        [createChildren, onKeyDown, onFocus]
    );
}
